import React from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { useCreateRatingSpreadMutation } from "../../../redux/service/modules/admin";
import { useNotification } from "../../../context/NotificationContext";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import CommonDialog from "../../../components/common/CommonDialog";

const RatingSpreadDialog = ({ open, onClose, title, ctaText }) => {
    const { control, handleSubmit, reset } = useForm();
    const [createRatingSpread, { isLoading: isCreating }] =
        useCreateRatingSpreadMutation();

    const { showNotification } = useNotification();

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            await createRatingSpread({ data: data })
                .unwrap()
                .then((response) => {
                    if (response) {
                        showNotification("success", response.message, "");
                        handleClose();
                    } else {
                        showNotification(
                            "error",
                            "An Error Occurred",
                            "Failed to Create/Update Rating Spread"
                        );
                        handleClose();
                    }
                })
                .catch((error) => {
                    if (error.status === 400) {
                        showNotification(
                            "error",
                            "An Error Occurred",
                            "Failed to Create/Update Rating Spread"
                        );
                        handleClose();
                    }
                });
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <div className="space-y-2">
                <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                    Index
                </label>
                <Controller
                    name="index"
                    control={control}
                    rules={{
                        required: "Index is required",
                    }}
                    render={({ field, fieldState }) => (
                        <div className="relative">
                            <select
                                {...field}
                                className="w-full xl:px-2 2xl:px-3 3xl:px-4 xl:py-1.5 2xl:py-2 3xl:py-2.5 xl:text-xs 2xl:text-sm 3xl:text-base
                                xl:border xl:rounded-md 2xl:border-2 2xl:rounded-lg 3xl:border-2 3xl:rounded-lg
                                border-gray-200 focus:outline-none xl:focus:ring-1 2xl:focus:ring-2 3xl:focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                            >
                                <option value="" disabled selected>
                                    Select Index
                                </option>
                                <option value="Sensex">Sensex</option>
                                <option value="Nifty">Nifty</option>
                                <option value="Nifty 100">Nifty 100</option>
                                <option value="Nifty 200">Nifty 200</option>
                                <option value="Nifty 500">Nifty 500</option>
                            </select>
                            {fieldState.error && (
                                <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                    {fieldState.error.message}
                                </span>
                            )}
                        </div>
                    )}
                />
            </div>

            <Controller
                name="no_of_stocks"
                control={control}
                rules={{
                    required: "No of Stocks is required",
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField
                            label="No of Stocks"
                            type="number"
                            {...field}
                            required
                        />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <Controller
                name="ff_mcap_percentage"
                control={control}
                rules={{
                    required: "Ff Mcap Percentage is required",
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField
                            label="Ff Mcap Percentage"
                            type="number"
                            {...field}
                            required
                        />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <Controller
                name="mdvt_3m_percentage"
                control={control}
                rules={{
                    required: "Mdvt 3m Percentage is required",
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField
                            label="Mdvt 3m Percentage"
                            type="number"
                            {...field}
                            required
                        />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <Controller
                name="mf"
                control={control}
                rules={{
                    required: "Mf is required",
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField
                            label="Mf"
                            type="number"
                            {...field}
                            required
                        />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <ActionButtons
                onCancel={handleClose}
                ctaText={ctaText}
                onSave={handleSubmit(onSubmit)}
                isLoading={isCreating}
            />
        </CommonDialog>
    );
};

RatingSpreadDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    title: PropTypes.string,
    ctaText: PropTypes.string,
};

export default RatingSpreadDialog;
